import * as React from 'react';
import { Outlet, useLocation, useParams } from 'react-router-dom';

import { SplitList, SplitPager, SplitView, useSheetHost, useSplit } from '@/design';
import type { IPage } from '../api/oversight-contracts';
import { pageOf, useAdminData } from './admin-page';
import { stillListed } from './list-origin';
import {
  originOf,
  originRowAt,
  type IListOrigin,
  openedFromList,
  openedFromResults,
  resultsListSearch,
  searchFrom,
  wayBack
} from './participant-link';

/**
 * A list and its records as one area of the Team Portal — People and the hubs, Responses and a
 * response, the SEC ADV register's firms and a firm's card, Users and an account's card — drawn in
 * the design system's `SplitView` (the two-portals design, §4.4 and §5). The route element over
 * the list's index and its record.
 *
 * **Which search the pane draws.** The one the record was opened from — the list's links leave it
 * in the history entry's state, as they always did for the card's way back — or, where the
 * record's own address carries a query, that one. The pane asks survey-api for it again, with the
 * list's own query builder, so the pane and the list it stands for are one query.
 */
export function ListArea({
  param,
  list,
  paneTitle,
  pane,
  columnFrom
}: {
  /** The route parameter naming the record — or several, for a list whose records are of more
   *  than one kind (People's persons and its roster cards, SR-414), the first set naming it. */
  param: string | readonly string[];
  /** The list's path — where Esc goes, under the search. */
  list: string;
  paneTitle: string;
  /** The pane, over the search it draws and the record open beside it. */
  pane: (search: string, selected: string) => React.ReactNode;
  /** The narrowest window, in px, with room for the record beside the column (Reports, 1280). */
  columnFrom?: number;
}) {
  const params = useParams();
  const location = useLocation();
  const selected = (typeof param === 'string' ? [param] : param)
    .map((name) => params[name])
    .find((value) => value !== undefined);
  const open = selected !== undefined;
  // A record opened from the search results page (SR-463) has that page's address as its search:
  // the list beside it reads the words and cycle alone, and Esc goes back to the results.
  const results = openedFromResults(location.state);
  const from = searchFrom(location.state);
  const carried = (results ? resultsListSearch(from) : from) || (open ? location.search : '');
  const listInitially = open && (openedFromList(location.state) || location.search !== '');
  const [rereads, setRereads] = React.useState(0);
  // A write the record says changed the list: the column reads again, and — the record in a sheet
  // over the list page — the page refreshes once the sheet closes (`RecordSheetHost`).
  const sheetHost = useSheetHost();
  const reread = React.useCallback(() => {
    setRereads((count) => count + 1);
    sheetHost?.onWrite();
  }, [sheetHost]);
  const context = React.useMemo(() => ({ rereads, reread }), [rereads, reread]);
  // Opened from another area's list (SR-530 — Reinstatement, Registrations, Search results): that
  // list is the column, the way back, and the page under a sheet.
  const origin = open ? originOf(location.state) : null;

  return (
    <ListRereadContext.Provider value={context}>
      <SplitView
        record={open}
        listInitially={listInitially || origin !== null}
        paneTitle={origin ? origin.title : paneTitle}
        pane={
          origin ? (
            <OriginColumn origin={origin} state={location.state} pathname={location.pathname} />
          ) : open ? (
            pane(carried, selected)
          ) : null
        }
        closeTo={origin ? origin.back : results ? wayBack(location.state, list, '', from).to : `${list}${carried}`}
        columnFrom={columnFrom}
        writes={rereads}
        snapshot={origin !== null}
      >
        <Outlet />
      </SplitView>
    </ListRereadContext.Provider>
  );
}

/** The column of a record opened from another area's list: that list's rows as they were drawn,
 *  each a link that keeps the origin, the open one current. */
function OriginColumn({ origin, state, pathname }: { origin: IListOrigin; state: unknown; pathname: string }) {
  const current = originRowAt(origin, pathname);
  // After a write on the open record (`useListReread`), the list is asked whether the record is
  // still on it — one small read; gone, its row is done (dimmed, "Done"), still walked by ↑ / ↓.
  const rereads = React.useContext(ListRereadContext).rereads;
  const markDone = useSplit()?.markDone;
  const asked = React.useRef(rereads);
  // The record written to, asked about even once the reader has moved on to the next one; only
  // leaving the column drops the question.
  const latest = React.useRef({ recheck: origin.recheck, current, markDone });
  latest.current = { recheck: origin.recheck, current, markDone };
  const pending = React.useRef(new Set<AbortController>());
  React.useEffect(() => {
    const out = pending.current;
    return () => out.forEach((controller) => controller.abort());
  }, []);
  React.useEffect(() => {
    if (rereads === asked.current) return;
    asked.current = rereads;
    const { recheck, current: row, markDone: mark } = latest.current;
    if (!recheck || !row || !mark) return;
    const controller = new AbortController();
    pending.current.add(controller);
    void stillListed(recheck, row, controller.signal).then((listed) => {
      pending.current.delete(controller);
      if (!listed && !controller.signal.aborted) mark(row.key);
    });
  }, [rereads]);
  const items = React.useMemo(
    () =>
      origin.items.map((item) => ({
        key: item.key,
        to: item.to,
        state,
        primary: item.primary,
        secondary: item.secondary
      })),
    [origin, state]
  );
  return (
    <SplitList
      label={origin.title}
      items={items}
      selected={current?.key ?? null}
      status={origin.items.length === 0 ? 'The list was empty.' : undefined}
    />
  );
}

/**
 * A record's way to have the list beside it read again — after a write that changes what the
 * list draws of it (a work item's state, its assignee). The pane's read counts the requests in
 * its dependencies and not in the query it answers, so the rows already drawn stay on screen
 * while the new ones are out. A no-op outside a list area: a record drawn alone has no list.
 */
const ListRereadContext = React.createContext<{ rereads: number; reread: () => void }>({
  rereads: 0,
  reread: () => undefined
});

export function useListReread(): () => void {
  return React.useContext(ListRereadContext).reread;
}

/** How often the record has asked — for a pane whose list is no page (`usePaneRead`'s), to read
 *  again on it. */
export function useListRereads(): number {
  return React.useContext(ListRereadContext).rereads;
}

/**
 * The pane's read of the list: the list's query, built by the list from the search the pane
 * draws, at the page that search names until the pane turns it. What it answers is drawn only
 * under the query it answers, as on the list.
 *
 * It is read again whenever the record asks (`useListReread`), under the same query.
 *
 * `status` is what the pane says in place of rows: the read's failure, a page past the end of a
 * list that is not empty, or — for a list that is — `empty`, the list's own sentence. Undefined
 * while the read is out, so the pane draws its skeleton.
 */
/** A list survey-api answers whole, as the one page a pane draws (`usePaneRead`'s answer). */
export function wholePage<Row>(items: Row[]): IPage<Row> {
  return { items, page: 1, pageSize: Math.max(items.length, 1), total: items.length };
}

export function usePaneRead<Row>(
  search: string,
  empty: string,
  read: (params: URLSearchParams, page: number, signal: AbortSignal) => Promise<IPage<Row>>
) {
  const params = React.useMemo(() => new URLSearchParams(search), [search]);
  const [page, setPage] = React.useState(() => pageOf(params.get('page')));
  React.useEffect(() => setPage(pageOf(params.get('page'))), [params]);
  const asked = `${search}\u0001${page}`;
  const { rereads } = React.useContext(ListRereadContext);
  const { data, error } = useAdminData<{ asked: string; page: IPage<Row> }>(
    async (signal) => ({ asked, page: await read(params, page, signal) }),
    [asked, rereads]
  );
  const shown = data?.asked === asked ? data.page : null;
  const pager = shown ? (
    <SplitPager
      page={shown.page}
      pageSize={shown.pageSize}
      total={shown.total}
      onPage={setPage}
    />
  ) : null;
  return { shown, pager, status: paneStatus(shown, error, empty) };
}

function paneStatus<Row>(
  shown: Nullable<IPage<Row>>,
  error: Nullable<string>,
  empty: string
): string | undefined {
  if (error !== null) return error;
  if (shown === null || shown.items.length > 0) return undefined;
  return shown.total > 0 ? 'This page is past the end of the list.' : empty;
}
